import React, { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { KeyRound, Loader2, LogOut } from 'lucide-react';
import { BrandBadge } from '@/components/BrandLogo';

/**
 * Full-page interstitial that forces the user to change their password before
 * they can access the rest of the app. Rendered by AppShell when the current
 * user has `must_change_password: true`.
 */
export default function ForcePasswordChange() {
  const { user, refreshMe, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (next !== confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password changed — welcome to HearClear OS');
      await refreshMe();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center p-6"
      data-testid="force-password-change-page">
      <Card className="max-w-md w-full p-6 border-stone-200">
        <div className="flex items-center gap-2 mb-4">
          <BrandBadge size={32} />
          <div>
            <div className="font-heading text-lg font-bold text-stone-900">Set a new password</div>
            <div className="text-xs text-stone-500">Required before you can continue · {user?.email}</div>
          </div>
        </div>
        <div className="rounded border border-amber-200 bg-amber-50 text-amber-900 text-xs p-2.5 mb-4 flex items-start gap-2">
          <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            Your account was created with a temporary password. Please pick a new one you&apos;ll remember — it must be at least 6 characters.
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
              className="h-9 mt-1" required autoFocus data-testid="fpc-current" />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
              className="h-9 mt-1" required minLength={6} data-testid="fpc-new" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="h-9 mt-1" required minLength={6} data-testid="fpc-confirm" />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" disabled={busy}
              className="flex-1 bg-[color:var(--hc-primary,#2D3982)] hover:opacity-90 text-white"
              data-testid="fpc-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <KeyRound className="w-4 h-4 mr-1.5" />}
              Change password
            </Button>
            <Button type="button" variant="ghost" onClick={logout} data-testid="fpc-logout">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
